import {
    focusElement,
    focusableElements,
    inertOutside,
    lockPage,
    setPanelVisibility,
    trapFocus,
    unlockPage,
} from './shared';

export default class MobileNavigation {
    constructor(header, { onOpen, onStateChange }) {
        this.header = header;
        this.navigation = header.querySelector('[data-mobile-navigation]');
        this.toggle = header.querySelector('[data-mobile-toggle]');
        this.onOpen = onOpen;
        this.onStateChange = onStateChange;
        this.enabled = false;
        this.isOpen = false;
        this.stack = [];
        this.restoreInert = null;
        this.headerAttributes = null;

        if (!this.navigation || !this.toggle) {
            return;
        }

        this.panels = Array.from(this.navigation.querySelectorAll('[data-mobile-panel]'));
        this.rootPanel =
            this.panels.find((panel) => panel.id === 'mobile-menu-root') || this.panels[0];
        this.activePanel = this.rootPanel;
        this.openLabel =
            this.toggle.dataset.openLabel || this.toggle.getAttribute('aria-label') || 'Abrir menu';
        this.closeLabel = this.toggle.dataset.closeLabel || 'Fechar menu';
        this.toggle.addEventListener('click', () => {
            if (this.enabled) {
                this.isOpen ? this.close() : this.open();
            }
        });
        this.navigation.addEventListener('click', (event) => {
            const next = event.target.closest('[data-mobile-next]');
            const back = event.target.closest('[data-mobile-back]');
            const tab = event.target.closest('[data-mobile-tab]');

            if (next) {
                this.next(next);
            } else if (back) {
                this.back();
            } else if (tab) {
                this.selectTab(tab);
            } else if (event.target.closest('[data-mobile-close]')) {
                this.close();
            } else if (event.target.closest('a[href]')) {
                this.close({ restoreFocus: false, immediate: true });
            }
        });
        document.addEventListener('keydown', (event) => {
            if (!this.isOpen) {
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                this.close();
                return;
            }

            this.onTabKeydown(event);
            trapFocus(event, this.header);
        });
        document.addEventListener('focusin', () => {
            if (this.isOpen && !this.header.contains(document.activeElement)) {
                focusElement(this.toggle);
            }
        });
        this.reset();
    }

    setEnabled(enabled) {
        this.enabled = enabled;

        if (!enabled) {
            this.close({ immediate: true, restoreFocus: false });
        }
    }

    open() {
        if (!this.enabled || this.isOpen || !this.rootPanel) {
            return;
        }

        this.onOpen?.();
        this.reset();
        this.isOpen = true;
        this.toggle.setAttribute('aria-expanded', 'true');
        this.toggle.setAttribute('aria-label', this.closeLabel);
        this.headerAttributes = new Map(
            ['role', 'aria-modal', 'aria-label'].map((attribute) => [
                attribute,
                this.header.getAttribute(attribute),
            ])
        );
        this.header.setAttribute('role', 'dialog');
        this.header.setAttribute('aria-modal', 'true');
        this.header.setAttribute(
            'aria-label',
            this.navigation.getAttribute('aria-label') || this.openLabel
        );
        lockPage(this);
        this.restoreInert = inertOutside(this.header);
        setPanelVisibility(this.navigation, true);
        this.onStateChange?.();
        focusElement(focusableElements(this.activePanel)[0] || this.toggle);
    }

    close({ immediate = false, restoreFocus = true } = {}) {
        if (!this.navigation || !this.isOpen) {
            return;
        }

        this.isOpen = false;
        this.toggle.setAttribute('aria-expanded', 'false');
        this.toggle.setAttribute('aria-label', this.openLabel);
        this.headerAttributes?.forEach((value, attribute) => {
            if (value === null) {
                this.header.removeAttribute(attribute);
            } else {
                this.header.setAttribute(attribute, value);
            }
        });
        this.headerAttributes = null;
        this.restoreInert?.();
        this.restoreInert = null;
        unlockPage(this);
        this.onStateChange?.();

        if (restoreFocus) {
            focusElement(this.toggle);
        }

        setPanelVisibility(this.navigation, false, { immediate });
        this.reset();
    }

    reset() {
        this.stack = [];
        this.activePanel = this.rootPanel;
        this.panels.forEach((panel) => {
            const active = panel === this.rootPanel;
            panel.dataset.active = String(active);
            setPanelVisibility(panel, active, { immediate: true });
            panel.scrollTop = 0;
            panel.querySelectorAll('[role="tablist"]').forEach((list) => {
                const first = list.querySelector('[data-mobile-tab]');

                if (first) {
                    this.selectTab(first, false);
                }
            });
        });
        this.navigation.querySelectorAll('[data-mobile-next]').forEach((button) => {
            button.setAttribute('aria-expanded', 'false');
        });
    }

    next(button) {
        const panel = document.getElementById(button.getAttribute('aria-controls'));

        if (!this.isOpen || !panel || !this.panels.includes(panel)) {
            return;
        }

        this.stack.push({ panel: this.activePanel, button, scrollTop: this.activePanel.scrollTop });
        button.setAttribute('aria-expanded', 'true');
        this.showLevel(panel);
        focusElement(panel.querySelector('[data-mobile-back]') || focusableElements(panel)[0]);
    }

    back() {
        const previous = this.stack.pop();

        if (!previous) {
            return;
        }

        previous.button.setAttribute('aria-expanded', 'false');
        this.showLevel(previous.panel);
        previous.panel.scrollTop = previous.scrollTop;
        focusElement(previous.button);
    }

    showLevel(panel) {
        this.activePanel.dataset.active = 'false';
        setPanelVisibility(this.activePanel, false, { immediate: true });
        this.activePanel = panel;
        panel.dataset.active = 'true';
        panel.scrollTop = 0;
        setPanelVisibility(panel, true);
    }

    selectTab(tab, animate = true) {
        const list = tab.closest('[role="tablist"]');

        if (!list) {
            return;
        }

        list.querySelectorAll('[data-mobile-tab]').forEach((candidate) => {
            const panel = document.getElementById(candidate.getAttribute('aria-controls'));
            const active = candidate === tab;
            candidate.setAttribute('aria-selected', String(active));
            candidate.tabIndex = active ? 0 : -1;

            if (panel && this.navigation.contains(panel)) {
                setPanelVisibility(panel, active, { immediate: !active || !animate, distance: 0 });
            }
        });
    }

    onTabKeydown(event) {
        const tab = event.target.closest('[data-mobile-tab]');

        if (
            !tab ||
            !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)
        ) {
            return;
        }

        const tabs = Array.from(
            tab.closest('[role="tablist"]').querySelectorAll('[data-mobile-tab]')
        );
        const index = tabs.indexOf(tab);
        let next = index;

        if (event.key === 'Home') {
            next = 0;
        } else if (event.key === 'End') {
            next = tabs.length - 1;
        } else {
            next =
                (index + (['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1) + tabs.length) %
                tabs.length;
        }

        event.preventDefault();
        this.selectTab(tabs[next]);
        focusElement(tabs[next]);
    }
}
